/**
 * ActionButtons - Form control buttons for validation actions
 * Based on requirements 5.1, 5.2, 5.3, 5.5
 */

import React, { useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';
import { useValidation } from '../../contexts/ValidationContext';
import { useUI } from '../../contexts/UIContext';
import { FieldChange } from '../../types';

// Styled components
const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
}));

const ChangesList = styled(List)(() => ({
  maxHeight: 300,
  overflow: 'auto',
  '& .MuiListItem-root': {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export interface ActionButtonsProps {
  className?: string;
  onSubmitOverride?: () => void;
  /** External in-flight flag for stage-managed submits — disables Submit + shows the spinner. */
  submitting?: boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ className, onSubmitOverride, submitting = false }) => {
  const { state, actions } = useValidation();
  const { actions: uiActions } = useUI();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Calculate changes summary
  const changesSummary = useMemo((): FieldChange[] => {
    if (!state.document) return [];

    return state.document.fields
      .filter(field => {
        const currentValue = state.validatedFields[field.id] || '';
        return currentValue !== field.value;
      })
      .map(field => ({
        fieldId: field.id,
        fieldName: field.name,
        originalValue: field.value,
        newValue: state.validatedFields[field.id] || '',
        confidence: field.confidence,
      }));
  }, [state.document, state.validatedFields]);

  // Determine if submit should be disabled
  // Only disable during submission, allow submit even with missing/invalid fields
  const isSubmitDisabled = state.isSubmitting || submitting;

  // Handle save action (auto-save is already implemented, this is manual save)
  const handleSave = useCallback(async () => {
    try {
      uiActions.addNotification({
        type: 'info',
        message: 'Saving changes...',
        autoHide: true,
        duration: 2000,
      });

      // In a real implementation, this would call an API to save draft
      await new Promise(resolve => setTimeout(resolve, 500));

      uiActions.addNotification({
        type: 'success',
        message: 'Changes saved successfully',
        autoHide: true,
        duration: 3000,
      });
    } catch (error) {
      uiActions.addNotification({
        type: 'error',
        message: 'Failed to save changes',
        autoHide: true,
        duration: 5000,
      });
    }
  }, [uiActions]);

  // Handle submit confirmation
  const handleSubmitClick = useCallback(() => {
    if (onSubmitOverride) {
      onSubmitOverride();
    } else {
      setShowConfirmDialog(true);
    }
  }, [onSubmitOverride]);

  // Handle confirmed submission
  const handleConfirmedSubmit = useCallback(async () => {
    setShowConfirmDialog(false);
    
    try {
      await actions.submitValidation();
      
      uiActions.addNotification({
        type: 'success',
        message: 'Document validation submitted successfully',
        autoHide: true,
        duration: 5000,
      });
    } catch (error) {
      uiActions.addNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to submit validation',
        autoHide: true,
        duration: 5000,
      });
    }
  }, [actions, uiActions]);

  // Handle reset form
  const handleReset = useCallback(() => {
    actions.resetForm();
    
    uiActions.addNotification({
      type: 'info',
      message: 'Form reset to original values',
      autoHide: true,
      duration: 3000,
    });
  }, [actions, uiActions]);

  // Handle dialog close
  const handleDialogClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // Get submit button text based on state
  const getSubmitButtonText = () => {
    if (state.isSubmitting || submitting) return 'Submitting...';
    return 'Submit';
  };

  return (
    <>
      <ButtonContainer className={className} data-hitl-actions>
        {/* Save Button */}
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!state.isDirty || state.isSubmitting}
        >
          Save Draft
        </Button>

        {/* Reset Button */}
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleReset}
          disabled={!state.isDirty || state.isSubmitting}
          color="secondary"
        >
          Reset
        </Button>

        {/* Spacer */}
        <Box flex={1} />

        {/* Submit Button — data-hitl-submit lets keyboard flow land here after the last field (UX4) */}
        <Button
          variant="contained"
          data-hitl-submit
          startIcon={(state.isSubmitting || submitting) ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSubmitClick}
          disabled={isSubmitDisabled}
          color="primary"
          size="large"
        >
          {getSubmitButtonText()}
        </Button>
      </ButtonContainer>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon color="primary" />
            Confirm Submission
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to submit the validation for this document. Please review the changes below:
          </Typography>

          {changesSummary.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Changes Summary ({changesSummary.length} field{changesSummary.length !== 1 ? 's' : ''})
              </Typography>
              
              <ChangesList>
                {changesSummary.map((change, index) => (
                  <React.Fragment key={change.fieldId}>
                    <ListItem>
                      <ListItemIcon>
                        <EditIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">
                              {change.fieldName}
                            </Typography>
                            <Chip
                              label={`${change.confidence}%`}
                              size="small"
                              color={change.confidence < 80 ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>From:</strong> "{change.originalValue || '(empty)'}"
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>To:</strong> "{change.newValue || '(empty)'}"
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < changesSummary.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </ChangesList>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No changes were made to the original extracted data.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Once submitted, this validation will be sent to the Augmentum/Momentum system for processing.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSubmit}
            variant="contained"
            startIcon={state.isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
            disabled={state.isSubmitting}
          >
            {state.isSubmitting ? 'Submitting...' : 'Confirm Submission'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionButtons;